import { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { View } from './View'

/**
 * IBA独自コンポーネントです
 */
export const Select = ({ itemKey, dataList, handleChangeSelect, searchPlaceholder, hasError, ...props }) => {
    const selectRef = useRef(null)
    const [active, setActive] = useState(false)
    const [searchValue, setSearchValue] = useState('')
    const [data, setData] = useState(dataList)

    /**
     * 入力欄クリック時の選択肢を表示非表示
     * @returns {void}
     */
    const onClick = () => {
        setActive((prevActive) => {
            return !prevActive
        })
    }

    const handleKeyDownInput = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            setActive((prevActive) => {
                return !prevActive
            })
        }
    }

    /**
     * クリアボタンクリック時、入力欄に反映
     * @returns {void}
     */
    const handleClickClearButton = (e) => {
        e.stopPropagation()
        handleChangeSelect && handleChangeSelect('')
    }

    /**
     * 選択肢クリック時、入力欄に反映して選択肢を非表示
     * @param {string} value
     * @returns {void}
     */
    const onItemClick = (value) => {
        const selectedObject = data.find((data) => {
            return data[itemKey] === value
        })
        const content = selectedObject.content
        handleChangeSelect && handleChangeSelect(value, content)
        setActive(false)
    }

    /**
     * 検索欄入力時、入力内容を検索欄に反映し入力内容で選択肢をフィルター
     * @param {string} value
     * @returns {void}
     */
    const onChange = (value) => {
        const lowerValue = value.toLowerCase()
        const filterData = dataList.filter(({ content }) => {
            return content.toLowerCase().includes(lowerValue)
        })
        setSearchValue(value)
        setData(filterData)
    }

    const searchInputProps = {
        placeholder: searchPlaceholder || 'Search...',
        value: searchValue,
        onChange
    }

    useEffect(() => {
        const handleClickDropdown = (e) => {
            const selectOption = selectRef.current
            if (!selectOption?.contains(e.target)) {
                setActive(false)
            }
        }
        window.addEventListener('click', handleClickDropdown)

        return () => {
            window.removeEventListener('click', handleClickDropdown)
        }
    }, [selectRef])

    useEffect(() => {
        setData(dataList)
    }, [dataList])

    return (
        <View
            itemKey={itemKey}
            isActive={active}
            selectRef={selectRef}
            onClick={onClick}
            handleKeyDownInput={handleKeyDownInput}
            handleClickClearButton={handleClickClearButton}
            searchInputProps={searchInputProps}
            data={data}
            onItemClick={onItemClick}
            hasError={hasError}
            {...props}
        />
    )
}

const dataListTypes = PropTypes.arrayOf(
    PropTypes.shape({
        content: PropTypes.node.isRequired
    })
)

Select.propTypes = {
    /**
     * dataList
     */
    dataList: dataListTypes,
    /**
     * アイテムのキーとするプロパティ名<br>
     * ※ data に格納しているいずれかのプロパティを指定
     */
    itemKey: PropTypes.string,
    /**
     * 選択肢クリック時の関数<br>
     * (value: string | number, content: string) => void
     */
    handleChangeSelect: PropTypes.func,
    /**
     * width
     */
    width: PropTypes.number,
    /**
     * セレクトの入力欄のprops
     */
    inputProps: PropTypes.shape({
        disabled: PropTypes.bool,
        label: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        className: PropTypes.any
    }),
    /**
     * 選択を解除する
     */
    isClear: PropTypes.bool,
    /**
     * 選択肢の検索を行う
     */
    isSearch: PropTypes.bool,
    /**
     * 検索欄のプレースホルダ
     */
    searchPlaceholder: PropTypes.string,
    /**
     * 選択状態にする値<br>
     * ※ `itemKey` で指定したプロパティの値を指定する
     */
    selectedValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /**
     * スタイル変更用のクラス名
     */
    className: PropTypes.any,
    /**
     * エラー状態
     */
    hasError: PropTypes.bool
}

Select.defaultProps = {
    itemKey: 'content',
    width: 200,
    dataList: []
}
